import {
  resolveScreeningReadiness,
  type ProfileCompletionBreakdownItem,
  type ProfileCompletionResult,
  type ProfileCompletionSection,
} from './profileCompletionScore';
import { resolveCandidateJourneyPhase } from './userProfile';

export type ProfileSectionNudge = {
  section: ProfileCompletionSection;
  title: string;
  message: string;
  percent: number;
  ctaLabel: string;
  ctaPath: string;
  positionClass: string;
};

const NUDGE_POSITION_CLASS: Record<ProfileCompletionSection, string> = {
  basicProfile: 'profile-section-nudge-card--pos-top-right',
  prompts: 'profile-section-nudge-card--pos-mid-right',
  portfolio: 'profile-section-nudge-card--pos-bottom-right',
  profilePhoto: 'profile-section-nudge-card--pos-top-left',
  resume: 'profile-section-nudge-card--pos-mid-left',
  languages: 'profile-section-nudge-card--pos-bottom-left',
  references: 'profile-section-nudge-card--pos-top-right',
  professionalLinks: 'profile-section-nudge-card--pos-mid-right',
  workCulture: 'profile-section-nudge-card--pos-bottom-right',
};

const PROFILE_SECTION_NUDGE_COPY: Record<
  ProfileCompletionSection,
  { title: string; message: string; ctaLabel: string; ctaPath: string }
> = {
  basicProfile: {
    title: 'Finish your profile basics',
    message:
      'Add your contact details and location so recruiters can reach you and improve screening readiness.',
    ctaLabel: 'Complete basics',
    ctaPath: '/user-profile?focus=basics',
  },
  prompts: {
    title: 'Complete your screening prompts',
    message:
      'Video prompts are the heart of SkipScreen. Finish your set with video, documents, and descriptions to improve screening readiness.',
    ctaLabel: 'Finish prompts',
    ctaPath: '/prompts',
  },
  portfolio: {
    title: 'Build your portfolio',
    message:
      'Your portfolio helps recruiters go deeper than prompts alone. Add projects, accomplishments, or an overview to improve screening readiness.',
    ctaLabel: 'Add portfolio',
    ctaPath: '/user-profile?tab=portfolio',
  },
  profilePhoto: {
    title: 'Add a profile photo',
    message:
      'A profile photo helps recruiters recognize you quickly. Add one to your SkipScreen profile to improve screening readiness.',
    ctaLabel: 'Add photo',
    ctaPath: '/user-profile?focus=profile-photo',
  },
  resume: {
    title: 'Upload your resume',
    message:
      'Many recruiters still review resumes alongside video prompts. Upload yours to improve screening readiness.',
    ctaLabel: 'Upload resume',
    ctaPath: '/user-profile?focus=resume',
  },
  languages: {
    title: 'Add languages you speak',
    message:
      'Listing languages helps global teams find the right fit. Add languages to your SkipScreen profile to improve screening readiness.',
    ctaLabel: 'Add languages',
    ctaPath: '/user-profile?focus=languages',
  },
  references: {
    title: 'Add professional references',
    message:
      'References are a great way to show others vouch for your work. Add references to your SkipScreen profile to improve screening readiness.',
    ctaLabel: 'Add references',
    ctaPath: '/user-profile?tab=prompts&focus=references',
  },
  professionalLinks: {
    title: 'Add professional links',
    message:
      'LinkedIn, GitHub, and portfolio links let recruiters verify your background quickly. Add them to improve screening readiness.',
    ctaLabel: 'Add links',
    ctaPath: '/user-profile?tab=prompts&focus=professional-links',
  },
  workCulture: {
    title: 'Share your ideal work culture',
    message:
      'Describing the work environment you thrive in helps recruiters match you with the right teams. Add it to improve screening readiness.',
    ctaLabel: 'Add work culture',
    ctaPath: '/user-profile?focus=work-culture',
  },
};

/** Post-setup app surfaces only — not get-started / create-prompt onboarding funnel. */
const PROFILE_NUDGE_ROUTES = ['/dashboard', '/user-profile', '/prompts'] as const;

export function isProfileNudgeRoute(pathname: string): boolean {
  return PROFILE_NUDGE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );
}

export function isProfileSectionIncomplete(item: ProfileCompletionBreakdownItem): boolean {
  return item.sectionPercent < 99.9 && item.earned < item.weight - 0.05;
}

export function getPrioritizedIncompleteSections(
  breakdown: ProfileCompletionBreakdownItem[]
): ProfileCompletionBreakdownItem[] {
  return breakdown
    .filter(isProfileSectionIncomplete)
    .sort((a, b) => b.weight - b.earned - (a.weight - a.earned));
}

export function resolveNextProfileSectionNudge(
  userId: number | string | undefined,
  result: ProfileCompletionResult
): ProfileSectionNudge | null {
  if (!userId || result.isFullyComplete) {
    return null;
  }

  const nextSection = getPrioritizedIncompleteSections(result.breakdown)[0];

  if (!nextSection) {
    return null;
  }

  const copy = PROFILE_SECTION_NUDGE_COPY[nextSection.section];

  return {
    section: nextSection.section,
    title: copy.title,
    message: copy.message,
    percent: result.percent,
    ctaLabel: copy.ctaLabel,
    ctaPath: copy.ctaPath,
    positionClass: NUDGE_POSITION_CLASS[nextSection.section],
  };
}

export function shouldShowProfileSectionNudges(
  user: Record<string, unknown> | null | undefined,
  result: ProfileCompletionResult,
  promptCount = 0
): boolean {
  if (!user || result.isFullyComplete) {
    return false;
  }

  const onboardingDone = user.onboardingCompleted === true || user.onboarding_completed === true;

  if (!onboardingDone) {
    return false;
  }

  // Only after prompt setup — when the candidate has dashboard access.
  return resolveCandidateJourneyPhase(user, promptCount) === 'ready';
}

export function buildProfileSectionNudge(
  user: Record<string, unknown> | null | undefined,
  prompts: unknown[] | null | undefined,
  portfolio: unknown,
  options?: { hiddenForSession?: boolean }
): ProfileSectionNudge | null {
  if (!user || options?.hiddenForSession) {
    return null;
  }

  const promptList = Array.isArray(prompts) ? prompts : [];
  const result = resolveScreeningReadiness(user, prompts, portfolio);
  if (!shouldShowProfileSectionNudges(user, result, promptList.length)) {
    return null;
  }

  const userId = user.id as number | string | undefined;
  return resolveNextProfileSectionNudge(userId, result);
}
